import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { FC } from 'react';

import Alert from 'primitives/Alert';
import Button from 'primitives/Button';

import { logout } from 'utils/auth';

const ErrorMessage: FC<{
  error?: any;
  title?: string;
  message?: string;
  refetch?: () => void;
  type?: 'standard' | 'alert';
  showLoginButton?: boolean;
}> = ({ error, title, message, refetch, type = 'standard', showLoginButton = true }) => {
  const checkIfInvalidOperation = () => {
    if (error && typeof error === 'object' && error.graphQLErrors) {
      const graphQLError = error.graphQLErrors[0];
      const code =
        graphQLError && graphQLError.extensions
          ? graphQLError.extensions.code
          : 'INTERNAL_SERVER_ERROR';
      return code === 'INVALID_OPERATION';
    }
    return false;
  };

  const getTitleFromGraphQLError = (): string | null => {
    if (
      error &&
      typeof error === 'object' &&
      error.graphQLErrors &&
      error.graphQLErrors.length > 0
    ) {
      const graphQLError = error.graphQLErrors[0];

      if (
        getErrorCode().startsWith('AUTH') ||
        getErrorCode().startsWith('VAL') ||
        getErrorCode().startsWith('BUS')
      ) {
        return graphQLError.message;
      }

      return graphQLError && graphQLError.extensions
        ? (graphQLError.extensions.title as string | null)
        : null;
    } else if (
      error &&
      typeof error === 'object' &&
      error.networkError &&
      error.networkError.result &&
      error.networkError.result.errors
    ) {
      const message: string = error.networkError.result.errors[0].message;

      if (
        getErrorCode().startsWith('AUTH') ||
        getErrorCode().startsWith('VAL') ||
        getErrorCode().startsWith('BUS')
      ) {
        return message;
      }

      return error.networkError.result.errors[0].extensions.title;
    }
    return null;
  };

  function getErrorCode() {
    let code = 'INTERNAL_SERVER_ERROR';

    if (
      error &&
      typeof error === 'object' &&
      error.graphQLErrors &&
      error.graphQLErrors.length > 0
    ) {
      const graphQLError = error.graphQLErrors[0];
      code =
        graphQLError && graphQLError.extensions
          ? graphQLError.extensions.code
          : 'INTERNAL_SERVER_ERROR';
    } else if (
      error &&
      typeof error === 'object' &&
      error.networkError &&
      error.networkError.result &&
      error.networkError.result.errors
    ) {
      code = error.networkError.result.errors[0].extensions.code;
    }

    return code;
  }

  if (type === 'alert') {
    return (
      <Alert
        type="error"
        title={title || getTitleFromGraphQLError() || 'Uh oh! Something went wrong'}
        message={message || `Error Code: ${getErrorCode()}`}
      />
    );
  }

  return (
    <div
      className={classNames(
        'my-2 min-h-96 w-full text-center flex flex-col justify-center rounded-lg p-4 col-span-full',
        checkIfInvalidOperation() ? 'bg-orange-50' : 'bg-red-50 '
      )}
    >
      <div
        className={classNames(
          'mx-auto h-24 w-24 p-4 rounded-full flex items-center justify-center',
          checkIfInvalidOperation() ? 'bg-orange-100' : 'bg-red-100'
        )}
      >
        <ExclamationTriangleIcon
          className={classNames(
            'h-16 w-16',
            checkIfInvalidOperation() ? 'text-orange-500' : 'text-red-500'
          )}
        />
      </div>
      <h2 className="mt-2 text-md lg:text-lg font-semibold leading-6 text-gray-900">
        {title || getTitleFromGraphQLError() || 'Uh oh! Something went wrong'}
      </h2>
      <p className="mt-1 text-sm text-gray-800">
        There was an issue processing this request. Please try again later.
      </p>
      <p className="mt-1 text-sm lg:text-md text-gray-800">Error Code: {getErrorCode()}</p>
      <div className="mx-auto mt-6">
        {refetch && (
          <Button variant="secondary" onClick={refetch}>
            Retry
          </Button>
        )}
        {showLoginButton && <Button onClick={logout}>Go to Login</Button>}
      </div>
    </div>
  );
};

export default ErrorMessage;
